/**
 * Shared, pure display formatters for deploy/rollback surfaces (IA slice 4 — lifted here,
 * charter A-12, once a third call site needed them: `DeploymentTimeline`/
 * `DeploymentDetailModal` used to keep their own identical copies "since it's a two-line
 * pure formatter, not shared business logic"; `MyServicesPage`'s "Last deploy"/"Running
 * revision" columns and the rollback dialog's revision line made that a third and fourth
 * copy, past the point duplication earns its keep). Neither function ever recomputes or
 * re-resolves a value — both render exactly what the server already sent (A-10).
 */

/** Truncates an already-delivered digest for display only (e.g. "sha256:a1a1a1a1a1a1…") —
 * the authoritative value is whatever the server sent, this never re-resolves it. */
export function shortDigest(digest: string): string {
  const sep = digest.indexOf(":");
  if (sep === -1) return digest;
  return `${digest.slice(0, sep)}:${digest.slice(sep + 1, sep + 13)}`;
}

/** Short, monotonic-clock-free relative rendering of an already-delivered timestamp, purely
 * a display convenience — callers that also want the exact time should pair this with a
 * `<time title=...>` carrying `new Date(iso).toLocaleString()`. */
export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diffMs);
  const MIN = 60_000;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;
  if (abs < MIN) return "just now";
  if (abs < HOUR) return `${Math.round(diffMs / MIN)}m ago`;
  if (abs < DAY) return `${Math.round(diffMs / HOUR)}h ago`;
  return `${Math.round(diffMs / DAY)}d ago`;
}
